import { useMemo } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { PriceSimulationCompensationTypeDto } from '@eneco/dc-be/client';
import { Button, Heading, InputSelect, RadioGroup, RadioTile, Stack, Stretch, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { METER_TYPE_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const COMPENSATON_OPTIONS: Record<string, PriceSimulationCompensationTypeDto> = {
  OFFTAKE: 'Offtake',
  COMPENSATION: 'Compensation',
  VALORISATION: 'Valorisation',
  INJECTION: 'Injection',
};

export const SolarInstallDateStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const onNextClick = () => {
    trackEventBE('pbt_postalCodeStep', {
      status: 'click',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
  };

  const compensationType = watch('compensationType');
  const regionInBelgium = watch('regionInBelgium');
  const meterType = watch('meterType');
  const solarInstallationDate = watch('solarPanelInstallDate');

  const filteredCompensationTypeList = useMemo(() => {
    const compensationOptionsList = fields.solarInstallationDateStep.compensationOptionsList.value.options;
    if (meterType === METER_TYPE_OPTIONS.DIGITAL && regionInBelgium === 'Walloon') {
      if (solarInstallationDate === 'BeforeJanuary2024') {
        return compensationOptionsList?.filter(x =>
          [COMPENSATON_OPTIONS.COMPENSATION, COMPENSATON_OPTIONS.VALORISATION].includes(
            x.name as PriceSimulationCompensationTypeDto,
          ),
        );
      }
      if (solarInstallationDate === 'AfterJanuary2024') return;
    }
    if (meterType === METER_TYPE_OPTIONS.ANALOG || meterType === METER_TYPE_OPTIONS['BI-DIRECTIONAL']) return;
    compensationOptionsList.filter(x =>
      [COMPENSATON_OPTIONS.COMPENSATION].includes(x.name as PriceSimulationCompensationTypeDto),
    );
  }, [fields.solarInstallationDateStep.compensationOptionsList, meterType, regionInBelgium, solarInstallationDate]);

  const handleChangeSolarInstallationDate = (onChange: (value: string) => void, newValue: string) => {
    if (newValue === 'AfterJanuary2024') {
      // Offtake and Injection == Compensation
      setValue('compensationType', COMPENSATON_OPTIONS.COMPENSATION);
    }
    onChange(newValue);
  };

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.solarInstallationDateStep.title.value}
        </Heading>
        <Text>{fields.solarInstallationDateStep.subTitle.value}</Text>
      </Stack>
      <Stretch>
        <Stack gap={10} direction={'column'}>
          <Controller
            control={control}
            name={'solarPanelInstallDate'}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                name={'solarPanelInstallDate'}
                aria-labelledby={fields.solarInstallationDateStep.installationDateOptions.value.label}
                direction="column"
                value={value?.toString()}
                error={errors.solarPanelInstallDate?.message}
                onValueChange={newValue => handleChangeSolarInstallationDate(onChange, newValue)}>
                {fields.solarInstallationDateStep.installationDateOptions.value.options.map(option => {
                  return (
                    <RadioTile value={option.name} key={option.name}>
                      <Text>{option.label}</Text>
                    </RadioTile>
                  );
                })}
              </RadioGroup>
            )}
          />
          {solarInstallationDate === 'AfterJanuary2024' ? (
            <Stack gap="2">
              <Text weight="bold" size="BodyS">
                {fields.solarInstallationDateStep.compensationOptionsList.value.label}
              </Text>
              <Text>
                {
                  fields.solarInstallationDateStep.compensationOptionsList.value.options.find(
                    x => x.name === COMPENSATON_OPTIONS.OFFTAKE,
                  )?.label
                }{' '}
                &{' '}
                {
                  fields.solarInstallationDateStep.compensationOptionsList.value.options.find(
                    x => x.name === COMPENSATON_OPTIONS.INJECTION,
                  )?.label
                }
              </Text>
            </Stack>
          ) : (
            <InputSelect
              placeholder=""
              label={fields.solarInstallationDateStep.compensationOptionsList.value.label}
              hint={<RichText html={fields.solarInstallationDateStep.compensationOptionsList.value.hint} />}
              error={
                errors.compensationType?.message
                  ? fields.solarInstallationDateStep.compensationOptionsList.value.requiredMessage
                  : undefined
              }
              options={filteredCompensationTypeList?.map(x => ({ label: x.label, value: x.name })) ?? []}
              defaultValue={compensationType}
              isOptional={false}
              {...register('compensationType')}
            />
          )}
          <label htmlFor={'compensationType'}>
            <Stack>
              <Text weight="bold">{fields.solarInstallationDateStep.explanationTitle.value}</Text>
              <RichText html={fields.solarInstallationDateStep.explanationDescription.value} />
            </Stack>
          </label>
        </Stack>
      </Stretch>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
